import { useCallback, useEffect, useState } from 'react'
import { Button, Modal, TableProps, Tag, Dropdown, Menu, Card, Space, Typography, Badge } from 'antd'
import { EyeOutlined, DeleteOutlined, DownOutlined, EditOutlined, UserOutlined, SafetyOutlined, ReloadOutlined } from "@ant-design/icons"
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { apiDeleteRole, apiGetListRoles, apiReloadPolicy } from '../../api'
import { useLoading } from '../../context/LoadingContext'
import { IPermission, IRole, IRoles } from '../../models/role.model'
import { Link } from 'react-router-dom'
import { ADMIN, CREATE, ROLE, UPDATE } from '../../assets/constants/route_path'
import { toastError, toastSuccess } from '../../utils/toast'
import { permissionOptions } from '../../assets/constants/default_options'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import ModalDetailRole, { roleColors } from './ModalDetailRole'

const { Title } = Typography

export default function Role() {
    const [roles, setRoles] = useState<IRoles>({})
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [modalDelete, setModalDelete] = useState<{ data: IRole | null, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [modalDetail, setModalDetail] = useState<{ data: IRole | null, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [modalGroupDetail, setModalGroupDetail] = useState<{
        permissionName: string,
        groupName: string,
        permissions: { permission: IPermission, detail: any }[],
        isOpen: boolean
    }>({
        permissionName: '',
        groupName: '',
        permissions: [],
        isOpen: false,
    })

    const { setIsLoading } = useLoading()
    const authState = useSelector(authSelector)
    const { account } = authState

    const columns: TableProps<any>['columns'] = [
        {
            key: 'id',
            title: 'ID',
            dataIndex: 'id',
            width: 80,
            render: (id: number) => (
                <Badge
                    count={id}
                    style={{
                        backgroundColor: '#f0f0f0',
                        color: '#666',
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }}
                />
            ),
        },
        {
            key: 'name',
            title: (
                <Space>
                    <UserOutlined style={{ color: '#1890ff' }} />
                    <span>Tên phân quyền</span>
                </Space>
            ),
            render: (_, data: IRole) => (
                <Button
                    type='link'
                    onClick={() => setModalDetail({ data, isOpen: true })}
                    style={{
                        padding: '0',
                        height: 'auto',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#1890ff'
                    }}
                >
                    <Space>
                        <SafetyOutlined />
                        {data.name}
                    </Space>
                </Button>
            ),
        },
        {
            key: 'permissions',
            title: (
                <Space>
                    <SafetyOutlined style={{ color: '#722ed1' }} />
                    <span>Chức năng</span>
                </Space>
            ),
            render: (_, data: IRole) => {
                const groupedPermissions: { [key: string]: number } = {}
                const groupedPermissionsDetail: { [key: string]: { permission: IPermission, detail: any }[] } = {}
                data.permissions?.forEach((perm: IPermission) => {
                    const permissionDetail = permissionOptions
                        .find(item => item.value === `${perm.action}::${perm.resource}`)
                    const groupName = permissionDetail?.name || ''
                    groupedPermissions[groupName] = (groupedPermissions[groupName] || 0) + 1
                    if (!groupedPermissionsDetail[groupName]) {
                        groupedPermissionsDetail[groupName] = []
                    }
                    groupedPermissionsDetail[groupName].push({
                        permission: perm,
                        detail: permissionDetail
                    })
                })
                const handleGroupClick = (groupName: string) => {
                    setModalGroupDetail({
                        permissionName: data.name || '',
                        groupName,
                        permissions: groupedPermissionsDetail[groupName],
                        isOpen: true
                    })
                }

                return (
                    <div style={{ maxWidth: '400px' }}>
                        {Object.entries(groupedPermissions).map(([groupName, count], index) => (
                            <Tag
                                key={index}
                                color={roleColors[index % roleColors.length]}
                                style={{
                                    margin: '2px',
                                    cursor: 'pointer',
                                    borderRadius: '12px',
                                    fontWeight: '500',
                                    fontSize: '12px',
                                    padding: '4px 12px',
                                    border: 'none',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => handleGroupClick(groupName)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)'
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)'
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {groupName} ({count})
                            </Tag>
                        ))}
                    </div>
                )
            },
        },
        {
            key: 'action',
            title: 'Tác vụ',
            dataIndex: 'action',
            align: 'center',
            width: 120,
            render: (_, data: IRole) => (
                <Dropdown
                    overlay={
                        <Menu style={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            <Menu.Item key="detail">
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => setModalDetail({ data, isOpen: true })}
                                    style={{
                                        width: '100%',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Chi tiết
                                </Button>
                            </Menu.Item>
                            {account.canUpdateRole && <Menu.Item key="edit">
                                <Link state={data} to={ADMIN + ROLE + UPDATE}>
                                    <Button
                                        style={{
                                            width: '100%',
                                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontWeight: '500'
                                        }}
                                        icon={<EditOutlined />}
                                    >
                                        Sửa
                                    </Button>
                                </Link>
                            </Menu.Item>}
                            {account.canDeleteRole && <Menu.Item key="delete">
                                <Button
                                    style={{
                                        width: '100%',
                                        background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
                                        color: '#d32f2f',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontWeight: '500'
                                    }}
                                    onClick={() => setModalDelete({ data, isOpen: true })}
                                    icon={<DeleteOutlined />}
                                >
                                    Xoá
                                </Button>
                            </Menu.Item>}
                        </Menu>
                    }
                    trigger={['click']}
                >
                    <Button
                        style={{
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                            border: 'none',
                            fontWeight: '500',
                            boxShadow: '0 2px 8px rgba(79, 172, 254, 0.3)'
                        }}
                    >
                        Chức năng <DownOutlined />
                    </Button>
                </Dropdown>
            ),
        },
    ]

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resRoles = await apiGetListRoles({
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
            })
            if (resRoles.data != null) {
                const listRoles = resRoles.data.roles.filter((item: IRole) => item.name !== 'Super Admin')
                resRoles.data.roles = listRoles
                resRoles.data.count--
                setRoles(resRoles.data)
            } else {
                setRoles({})
            }
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            console.log(error)
        }
    }, [setIsLoading, currentPage, pageSize])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleDelete = async () => {
        try {
            setIsLoading(true)
            const resp = await apiDeleteRole(modalDelete.data?.id)
            if (resp.status === 200) {
                setModalDelete({ data: null, isOpen: false })
                toastSuccess('Xoá phân quyền thành công!')
                fetchData()
            } else {
                toastError('Có lỗi xảy ra!')
            }
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
            setIsLoading(false)
        }
    }

    const reloadPolicy = async () => {
        try {
            setIsLoading(true)
            await apiReloadPolicy()
            toastSuccess('Reload danh sách quyền thành công!')
            fetchData()
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <Card>
                {/* Header Section */}
                <div style={{
                    marginBottom: '24px',
                    padding: '20px 0',
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <Title level={3} style={{
                                margin: 0,
                                color: '#1a1a1a',
                                fontWeight: '600'
                            }}>
                                <SafetyOutlined style={{ marginRight: '12px', color: '#722ed1' }} />
                                Quản lý phân quyền
                            </Title>
                            <p style={{
                                marginTop: 8,
                                color: '#666',
                            }}>
                                Quản lý và phân quyền cho các vai trò trong hệ thống
                            </p>
                        </div>
                        <Space>
                            {account.canReloadBasePermissions && (
                                <Button
                                    onClick={reloadPolicy}
                                    type="dashed"
                                >
                                    <ReloadOutlined /> Reload
                                </Button>
                            )}
                            {account.canAddRole && (
                                <Link to={ADMIN + ROLE + CREATE}>
                                    <Button
                                        type="primary"
                                        style={{
                                            background: 'linear-gradient(#e51623 0% 0%, #5f0d0d 100%)',
                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                                        }}
                                    >
                                        <UserOutlined /> Thêm phân quyền
                                    </Button>
                                </Link>
                            )}
                        </Space>
                    </div>
                </div>

                {/* Table Section */}
                <CustomTable
                    namePage="phân quyền"
                    columns={columns}
                    dataSource={roles.roles?.sort((a: IRole, b: IRole) => Number(a.id) - Number(b.id))}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    total={roles.count}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                />
            </Card>
            <Modal
                title={
                    <div style={{ textAlign: 'center' }}>
                        <DeleteOutlined style={{
                            fontSize: '24px',
                            color: '#ff4d4f',
                            marginBottom: '8px'
                        }} />
                        <div style={{ fontSize: '18px', fontWeight: '600' }}>
                            Xác nhận xoá phân quyền
                        </div>
                    </div>
                }
                open={modalDelete.isOpen}
                onCancel={() => setModalDelete({ data: null, isOpen: false })}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => setModalDelete({ data: null, isOpen: false })}
                        style={{ marginRight: '8px' }}
                    >
                        Huỷ
                    </Button>,
                    <Button
                        key="delete"
                        onClick={handleDelete}
                        style={{
                            background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                            color: '#d32f2f',
                            border: 'none',
                            fontWeight: '500'
                        }}
                    >
                        <DeleteOutlined /> Xoá
                    </Button>,
                ]}
                style={{ textAlign: 'center' }}
            >
                <div style={{
                    padding: '20px 0',
                    fontSize: '16px'
                }}>
                    Bạn có chắc muốn xoá phân quyền <strong style={{ color: '#722ed1' }}>{modalDelete.data?.name}</strong>?
                    <br />
                    <span style={{ color: '#666', fontSize: '14px' }}>
                        Hành động này không thể hoàn tác
                    </span>
                </div>
            </Modal>
            <ModalDetailRole
                detail={modalDetail}
                setDetail={setModalDetail}
            />
            <Modal
                title={
                    <div style={{
                        textAlign: 'center',
                        padding: '16px 0',
                        background: 'linear-gradient(#e51623 0% 0%, #5f0d0d 100%)',
                        margin: '-16px -24px 20px -24px',
                        color: 'white'
                    }}>
                        <SafetyOutlined style={{
                            fontSize: '24px',
                        }} />
                        <div style={{ fontSize: '18px', fontWeight: '600' }}>
                            {modalGroupDetail.permissionName}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>
                            Chi tiết nhóm quyền
                        </div>
                    </div>
                }
                open={modalGroupDetail.isOpen}
                onCancel={() => setModalGroupDetail({ permissionName: '', groupName: '', permissions: [], isOpen: false })}
                footer={[]}
                style={{ top: 20 }}
            >
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Card
                        style={{
                            borderRadius: '12px',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#4facfe',
                            marginBottom: '16px',
                            padding: '4px 8px',
                            background: 'linear-gradient(135deg, #4facfe15 0%, #00f2fe05 100%)',
                            borderRadius: '8px',
                            border: '1px solid #4facfe30'
                        }}>
                            <SafetyOutlined style={{ marginRight: '8px' }} />
                            {modalGroupDetail.groupName} ({modalGroupDetail.permissions.length})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {modalGroupDetail.permissions.map(({ detail }, index) => (
                                <Tag
                                    key={index}
                                    style={{
                                        margin: 0,
                                        padding: '4px 8px',
                                        borderRadius: '20px',
                                        background: '#4facfe20',
                                        color: '#4facfe',
                                        border: '1px solid #4facfe40',
                                        fontWeight: '500',
                                        fontSize: '13px'
                                    }}
                                >
                                    {detail?.label}
                                </Tag>
                            ))}
                        </div>
                    </Card>
                </div>
            </Modal>
        </div>
    )
}
